import { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useAxiosInstance } from "../../services/Interceptor";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

const COLLEGE_URL = process.env.REACT_APP_COLLEGE_URL || "";
let COLLEGE_ORIGIN = "";
try {
  COLLEGE_ORIGIN = COLLEGE_URL ? new URL(COLLEGE_URL).origin : "";
} catch {}

const AUTOLOGIN_TIMEOUT_MS = 10000;

function applySignIn(data, signIn, navigate) {
  if (!data?.accessToken || !data?.user) {
    toast.error("Invalid login token. Please try again.");
    return navigate("/sign-in");
  }
  if (data.user.role?.toLowerCase() !== "university") {
    toast.error("You do not have access to this section.");
    return navigate("/sign-in");
  }
  if (
    !signIn({
      auth: { token: data.accessToken, type: "Bearer" },
      refresh: data.refreshToken,
      expiresIn: data.expiresIn ?? 3600,
      userState: { user: data.user },
    })
  ) {
    toast.error("Error during sign-in.");
    return navigate("/sign-in");
  }
  navigate("/university/dashboard", { replace: true });
}

function CollegePlatformButton({ variant }) {
  const authHeader = useAuthHeader();
  const { user } = useAuthUser() || {};
  const cleanupRef = useRef(null);

  useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  const handleClick = useCallback(() => {
    const token = authHeader?.split(" ")[1];
    if (!COLLEGE_URL || !token) return;
    const win = window.open(`${COLLEGE_URL}/auto-login`, "_blank");
    if (!win) return;
    cleanupRef.current?.();
    let timeout;
    const listener = (e) => {
      if (
        e.origin !== COLLEGE_ORIGIN ||
        e.data?.type !== "COLLEGE_AUTOLOGIN_READY"
      )
        return;
      win.postMessage(
        { type: "COLLEGE_AUTOLOGIN_TOKEN", token },
        COLLEGE_ORIGIN,
      );
      window.removeEventListener("message", listener);
      clearTimeout(timeout);
      cleanupRef.current = null;
    };
    window.addEventListener("message", listener);
    timeout = setTimeout(() => {
      window.removeEventListener("message", listener);
      cleanupRef.current = null;
    }, AUTOLOGIN_TIMEOUT_MS);
    cleanupRef.current = () => {
      window.removeEventListener("message", listener);
      clearTimeout(timeout);
    };
  }, [authHeader]);

  if (
    user?.role !== "university" ||
    process.env.REACT_APP_SHOW_AUTO_LOGIN_LINK !== "yes"
  )
    return null;

  const icon = (
    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-1" />
  );
  const nav =
    "text-xl uppercase text-white my-auto hover:text-red-500 hover:underline";
  const btn = (cls) => (
    <button onClick={handleClick} className={cls}>
      College Platform {icon}
    </button>
  );

  if (variant === "mobile-dropdown")
    return (
      <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
        {btn("text-gray-700 text-lg")}
      </li>
    );
  return btn(
    variant === "mobile-menu" ? `${nav} pb-8 block w-full text-left` : nav,
  );
}

function AutoLoginPage() {
  const apiInstance = useAxiosInstance();
  const signIn = useSignIn();
  const navigate = useNavigate();

  useEffect(() => {
    if (window.opener) {
      if (!COLLEGE_ORIGIN) {
        toast.error("College platform URL is not configured.");
        navigate("/sign-in");
        return;
      }
      let timeout;
      const handleToken = async (e) => {
        if (
          e.origin !== COLLEGE_ORIGIN ||
          e.data?.type !== "HS_AUTOLOGIN_TOKEN"
        )
          return;
        window.removeEventListener("message", handleToken);
        clearTimeout(timeout);
        try {
          const { data } = await apiInstance.post("/user/token-login", {
            code: e.data.token,
          });
          applySignIn(data, signIn, navigate);
        } catch {
          toast.error("An error occurred during the login process.");
          navigate("/sign-in");
        }
      };
      window.addEventListener("message", handleToken);
      timeout = setTimeout(
        () => window.removeEventListener("message", handleToken),
        AUTOLOGIN_TIMEOUT_MS,
      );
      window.opener.postMessage({ type: "HS_AUTOLOGIN_READY" }, COLLEGE_ORIGIN);
      return () => {
        window.removeEventListener("message", handleToken);
        clearTimeout(timeout);
      };
    }

    // Cookie-based flow (direct access, no opener)
    apiInstance
      .post("/user/token-login", {}, { withCredentials: true })
      .then(({ data }) => applySignIn(data, signIn, navigate))
      .catch(() => {
        toast.error("An error occurred during the login process.");
        navigate("/sign-in");
      });
  }, [apiInstance, signIn, navigate]); // eslint-disable-line

  return null;
}

export default function AutoLogin({ navButton, variant }) {
  return navButton ? (
    <CollegePlatformButton variant={variant} />
  ) : (
    <AutoLoginPage />
  );
}
